import {
    Box,
    Heading,
    Divider,
    Button,
    VStack,
    HStack,
    useBreakpointValue,
    Spacer,
    Text,
    Link,
    Center
} from "@chakra-ui/react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button as ShadcnButton } from "@/components/ui/button"
import useMemberStore from "@/store/MemberStore"
import * as amplitude from "@amplitude/analytics-browser"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AddIcon } from '@chakra-ui/icons'
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"

import { MultiSelect } from "@/components/ui/multi-select"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import Head from "next/head"
import DashboardKeywordChart from "@/components/Keyword/DashboardKeywordChart"
import DashboardKeywordList from "@/components/Keyword/DashboardKeywordList"
import CreateKeywordComponent from "@/components/Keyword/CreateKeywordComponent"
import useKeywordListStore from "@/store/KeywordListStore"
import useKeywordPermissionStore from "@/store/KeywordPermissionStore"
import useKeywordDashboardStore from "@/store/KeywordDashboardStore"
import NextLink from "next/link"
import { KEYWORD_TIME_RANGE_MAP } from "@/constants/VipPlanDefinitions"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"
import ThreadPostList from "@/components/Keyword/KeywordThreadsPosts"
import {
    KEYWORD_DASHBOARD_PAGE_BG,
    SEGMENT_LIST_CLASS,
    SEGMENT_TRIGGER_CLASS,
    SEGMENT_TRIGGER_COMPACT_CLASS,
} from "@/constants/KeywordDashboardTheme"

function ThreadsKeywordDashboard() {
    const t = useTranslations("KeywordDashboard")
    const title = t("Meta.title")
    const isLoggedIn = useMemberStore((state) => state.checkIsLoggedIn())
    const member = useMemberStore((state) => state.member)
    const subscribedVipPlan = useMemberStore((state) => state.getSubscribedVipPlan())
    const router = useRouter()
    const { keywords: storeKeywords, updateInterval, fetchKeywords, isLoading: keywordsLoading } = useKeywordListStore()
    const [activeTab, setActiveTab] = useState("dashboard")
    const margin = useBreakpointValue({ base: "16px", md: "0px" })
    const [isLoading, setIsLoading] = useState(false)
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [showPermissionAlert, setShowPermissionAlert] = useState(false)
    const [showConnectAlert, setShowConnectAlert] = useState(false)
    const checkPermission = useKeywordPermissionStore(state => state.checkPermission)
    const hasPermission = useKeywordPermissionStore(state => state.hasPermission)
    const getCurrentSelectedThreadsAccountId = useMemberStore(state => state.getCurrentSelectedThreadsAccountId)
    const { chart, posts, page, hasMore, fetchDashboard, isPaginationLoading } = useKeywordDashboardStore()
    const [timeRange, setTimeRange] = useState(3)
    const [selectedKeywords, setSelectedKeywords] = useState([])
    const [activeKeywords, setActiveKeywords] = useState([])

    useEffect(() => {
        if (!isLoggedIn) {
            router.push("/signup")
            return
        }
        fetchKeywords()
    }, [isLoggedIn, router, fetchKeywords])

    useEffect(() => {
        if (storeKeywords.length === 0) {
            setActiveTab("keywords")
        } else if (storeKeywords.length > 0 && selectedKeywords.length === 0 && activeKeywords.length === 0) {
            setActiveTab("dashboard")
            // Select top 3 keywords by default
            const topKeywords = storeKeywords.slice(0, 3).map(keyword => keyword.keyword)
            setSelectedKeywords(topKeywords)
            setActiveKeywords(topKeywords)
            fetchDashboard(topKeywords, timeRange)
        }
    }, [storeKeywords, fetchDashboard, timeRange])

    const handlePermissionCheck = async () => {
        const hasConnectedAccount = member?.connectedThreadsAccounts?.length > 0
        const plan = member?.subscribedVipPlan

        if (!hasConnectedAccount && plan != "pro") {
            setShowConnectAlert(true)
            return
        }

        if (hasPermission) {
            setIsCreateDialogOpen(true)
            return
        }

        const threadsUserId = getCurrentSelectedThreadsAccountId()
        setIsLoading(true)
        const permissionResult = await checkPermission(threadsUserId)
        if (permissionResult) {
            setIsCreateDialogOpen(true)
        } else {
            setShowPermissionAlert(true)
        }
        setIsLoading(false)
    }

    const handleTimeRangeChange = (value) => {
        const newTimeRange = Number(value)
        const rangeLimit = KEYWORD_TIME_RANGE_MAP[subscribedVipPlan] ?? 3
        if (newTimeRange > rangeLimit) {
            toast.error(t("Message.timeRangeLimit", { timeRange: rangeLimit }), {
                action: {
                    label: t("Action.learnMore"),
                    onClick: () => {
                        amplitude.track("keyword_dashboard_time_range_limit_click")
                        router.push("/pricing")
                    }
                },
                style: {
                    background: '#f59e0b',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    padding: '16px',
                    borderRadius: '8px'
                }
            })
            return
        }

        setTimeRange(newTimeRange)
        if (activeKeywords.length > 0) {
            fetchDashboard(activeKeywords, newTimeRange)
        }
        amplitude.track("keyword_dashboard_time_range_change", {
            timeRange: newTimeRange
        })
    }

    const handleKeywordSelect = (values) => {
        setSelectedKeywords(values)
    }

    const handleLoadMore = () => {
        if (hasMore) {
            fetchDashboard(activeKeywords, timeRange, page + 1)
            amplitude.track("keyword_dashboard_load_more")
        }
    }

    const handleConfirmSelection = () => {
        setActiveKeywords(selectedKeywords)
        fetchDashboard(selectedKeywords, timeRange)
        amplitude.track("keyword_selection_changed", {
            selectedKeywords: selectedKeywords,
            timeRange
        })
    }
    return (
        <Box
            bg={KEYWORD_DASHBOARD_PAGE_BG}
            minH="100vh"
            paddingLeft={margin}
            paddingRight={margin}
            paddingBottom="48px"
        >
            <Toaster />
            <Head>
                <title>{title}</title>
                <meta name="og:title" content={title} />
            </Head>
            {showPermissionAlert && (
                <NoPermissionAlert onClose={() => setShowPermissionAlert(false)} />
            )}
            {showConnectAlert && (
                <NoConnectedAccountAlert onClose={() => setShowConnectAlert(false)} />
            )}
            <AlertDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("Actions.addKeyword")}</AlertDialogTitle>
                    </AlertDialogHeader>
                    <CreateKeywordComponent onClose={() => setIsCreateDialogOpen(false)} />
                </AlertDialogContent>
            </AlertDialog>
            <Box mx="auto" w="100%" marginTop="36px">
                <VStack spacing="16px" marginLeft="4px" marginRight="4px">
                    <Box width="100%">
                        <Heading size="lg" color="text.dark">{title}</Heading>
                        <Text fontSize="md" color="text.semidark">{t("description")}</Text>
                        <Box mt={4}>
                            <Tabs value={activeTab} onValueChange={setActiveTab}>
                                <HStack>
                                    <TabsList className={SEGMENT_LIST_CLASS}>
                                        <TabsTrigger className={SEGMENT_TRIGGER_CLASS} value="dashboard">{t("Title.dashboard")}</TabsTrigger>
                                        <TabsTrigger className={SEGMENT_TRIGGER_CLASS} value="keywords">{t("Title.keywordList")}</TabsTrigger>
                                    </TabsList>
                                    <Spacer />
                                    <Button
                                        variant="danger"
                                        leftIcon={<AddIcon />}
                                        onClick={handlePermissionCheck}
                                        isLoading={isLoading}
                                        loadingText={t("checkingPermission")}
                                    >
                                        {t("Actions.addKeyword")}
                                    </Button>
                                </HStack>
                                <Divider className="mt-4" />
                                <TabsContent value="dashboard">
                                    <Box>
                                        <HStack spacing={4} className="mb-4">
                                            <Tabs defaultValue={3} onValueChange={handleTimeRangeChange}>
                                                <TabsList className={SEGMENT_LIST_CLASS}>
                                                    <TabsTrigger className={SEGMENT_TRIGGER_COMPACT_CLASS} value={3}>{t("TimeRange.threeDays")}</TabsTrigger>
                                                    <TabsTrigger className={SEGMENT_TRIGGER_COMPACT_CLASS} value={7}>{t("TimeRange.sevenDays")}</TabsTrigger>
                                                    <TabsTrigger className={SEGMENT_TRIGGER_COMPACT_CLASS} value={14}>{t("TimeRange.fourteenDays")}</TabsTrigger>
                                                    <TabsTrigger className={SEGMENT_TRIGGER_COMPACT_CLASS} value={30}>{t("TimeRange.thirtyDays")}</TabsTrigger>
                                                    <TabsTrigger className={SEGMENT_TRIGGER_COMPACT_CLASS} value={90}>{t("TimeRange.ninetyDays")}</TabsTrigger>
                                                    <TabsTrigger className={SEGMENT_TRIGGER_COMPACT_CLASS} value={180}>{t("TimeRange.sixMonths")}</TabsTrigger>
                                                </TabsList>
                                            </Tabs>
                                            <Spacer />
                                            <HStack>
                                                <MultiSelect
                                                    options={storeKeywords.map(keyword => ({
                                                        value: keyword.keyword,
                                                        label: keyword.keyword
                                                    }))}
                                                    defaultValue={selectedKeywords}
                                                    onValueChange={handleKeywordSelect}
                                                    placeholder={t("Actions.selectKeywords")}
                                                />
                                                <ShadcnButton
                                                    onClick={handleConfirmSelection}
                                                    disabled={selectedKeywords.length === 0}
                                                >
                                                    {t("Actions.confirm")}
                                                </ShadcnButton>
                                            </HStack>
                                        </HStack>
                                        <DashboardKeywordChart chart={chart} />
                                        {selectedKeywords.length > 0 && (
                                            <Box marginTop="32px">
                                                <ThreadPostList posts={posts} showKeywordBadge={true} />
                                                <Center mt={4} mb={16}>
                                                    <Button
                                                        onClick={handleLoadMore}
                                                        isLoading={isPaginationLoading}
                                                        loadingText={t("Message.loading")}
                                                        isDisabled={!hasMore}
                                                        bg="black"
                                                        color="white"
                                                    >
                                                        {t("Action.loadMore")}
                                                    </Button>
                                                </Center>
                                            </Box>
                                        )}
                                    </Box>
                                </TabsContent>
                                <TabsContent value="keywords">
                                    <Box>
                                        <VStack spacing="16px" align="left">
                                            <DashboardKeywordList />
                                            {updateInterval > 0 && (
                                                <Alert>
                                                    <AlertTitle>{t("Title.updateIntervalNotice")}</AlertTitle>
                                                    <AlertDescription>
                                                        {t("Description.updateIntervalNotice", { updateInterval: updateInterval / 3600 })}
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </VStack>
                                    </Box>
                                </TabsContent>
                            </Tabs>
                        </Box>
                    </Box>
                </VStack>
            </Box>
        </Box>
    )
}

function NoPermissionAlert({ onClose }) {
    const t = useTranslations("KeywordCreate")
    const router = useRouter()
    const { locale } = router
    const redirectUri =
        locale == "zh-TW"
            ? `${process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_TW}?redirect=keyword`
            : `${process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_EN}?redirect=keyword`
    const url = `${process.env.NEXT_PUBLIC_THREADS_CALLBACK_WITH_SEARCH_URI}&redirect_uri=${redirectUri}`

    return (
        <AlertDialog open>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t("Alerts.needPermission.title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t("Alerts.needPermission.message")}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>
                        {t("Action.cancel")}
                    </AlertDialogCancel>
                    <Link href={url}>
                        <Button background="main.red">
                            {t("Alerts.needPermission.action")}
                        </Button>
                    </Link>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

function NoConnectedAccountAlert({ onClose }) {
    const t = useTranslations("KeywordDashboard")
    return (
        <AlertDialog open>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t("Alerts.needConnectAccount.title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t("Alerts.needConnectAccount.message")}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                    <AlertDialogCancel onClick={onClose}>
                        {t("Actions.cancel")}
                    </AlertDialogCancel>
                    <Link href="https://instagram.com/threadslytics">
                        <AlertDialogAction>
                            {t("Actions.contactUs")}
                        </AlertDialogAction>
                    </Link>
                    <Link href='/threads/connect' as={NextLink}>
                        <AlertDialogAction>
                            {t("Actions.connect")}
                        </AlertDialogAction>
                    </Link>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

export async function getStaticProps(context) {
    const props = {
        messages: (await import(`@/messages/${context.locale}.json`)).default,
    }
    return { props }
}

export default ThreadsKeywordDashboard